/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  EstateV2Filter,
  SearchSortField,
  SearchSortOrder,
  SearchV2Filter,
} from "~/generated/graphql";
import { showError } from "~/services/toast";
import { useEstateRecords } from "./useEstateRecords";
import { useEstateSort } from "./useEstateSort";
import { useFilteredQueryState } from "./useQueryState";

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type QueryState = EstateV2Filter &
  SortingProp & {
    page: number;
    limit: number;
    tags: string[];
    metadata: string[];
    tab: number;
    query: string;
  };

type Props = {
  activeTab?: number;
  defaultFilters?: EstateV2Filter;
  skipFields?: string[];
  skip?: boolean;
};

export function useEstateFetch({
  activeTab,
  defaultFilters,
  skipFields = [],
  skip = false,
}: Props) {
  const { state, updateQueryState } = useFilteredQueryState(
    estateV2FilterFields.filter(
      (filterField) => !skipFields.includes(filterField)
    )
  );

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const {
    page = 0,
    limit = 20,
    field,
    order,
    tags,
    metadata,
    tab: ignoredTab,
    query,
    ...rawFilters
  }: QueryState = state;

  const { sortField, sortDirection } = useEstateSort({
    field,
    order,
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    updateQueryState,
  });

  const filtersEstate = {
    tags: convertTags(tags),
    metadata: convertTags(metadata),
    ...rawFilters,
  };

  // Temporary using skip property because of conflict between old and new estate records view
  // (not compatible url filters, so we have to skip query when other tab is opened).
  //  After removing old tab it can be switched to useResourcesNewQuery.
  return {
    result: useEstateRecords(
      {
        skip: (activeTab !== undefined && activeTab !== 0) || skip,
        variables: {
          limit,
          page,
          sort: {
            field: sortField,
            order: sortDirection,
          },
          filter: { ...filtersEstate, ...defaultFilters },
          ...(query === undefined ? {} : { query: query.toString() }),
        },
      },
      showError
    ),
    filter: { ...filtersEstate, ...defaultFilters },
    sort: {
      field: sortField,
      order: sortDirection,
    },
    query,
  };
}

function convertTags(tags?: string[]) {
  return tags?.map((tag) => {
    const keyValue = tag.split(",");
    return { key: keyValue[0] ?? "", values: [keyValue[1] ?? ""] };
  });
}

export const estateV2FilterFields: (
  | keyof SearchV2Filter
  | "field"
  | "limit"
  | "order"
  | "page"
  | "query"
)[] = [
  "provider",
  "providerExclude",
  "costFrom",
  "costTo",
  "costExclude",
  "unassigned",
  "unassignedExclude",
  "assignmentUpdatedFrom",
  "assignmentUpdatedTo",
  "assignmentUpdateExclude",
  "waste",
  "wastePolicy",
  "wastePolicyExclude",
  "co2eWaste",
  "account",
  "accountExclude",
  "application",
  "applicationExclude",
  "environment",
  "environmentExclude",
  "region",
  "regionExclude",
  "tags",
  "tagsExclude",
  "type",
  "typeExclude",
  "subtype",
  "subtypeExclude",
  "typeSubtype",
  "typeSubtypeExclude",
  "category",
  "categoryExclude",
  "page",
  "limit",
  "field",
  "order",
  "query",
  "externalIntegration",
  "externalIntegrationExclude",
  "enrichment",
  "enrichmentIntegrationId",
  "enrichmentIntegrationIdExclude",
  "metadata",
  "metadataExclude",
  "firstSeenAtFrom",
  "firstSeenAtExclude",
  "firstSeenAtTo",
  "lifeSpanFrom",
  "lifeSpanTo",
  "lifeSpanExclude",
  "service",
  "serviceExclude",
];

export const estateV2GroupByFields: (
  | keyof SearchV2Filter
  | "limit"
  | "page"
  | "query"
)[] = [
  "provider",
  "providerExclude",
  "costFrom",
  "costTo",
  "costExclude",
  "unassigned",
  "unassignedExclude",
  "assignmentUpdatedFrom",
  "assignmentUpdatedTo",
  "assignmentUpdateExclude",
  "waste",
  "wastePolicy",
  "wastePolicyExclude",
  "co2eWaste",
  "account",
  "accountExclude",
  "application",
  "applicationExclude",
  "environment",
  "environmentExclude",
  "region",
  "regionExclude",
  "tags",
  "tagsExclude",
  "type",
  "typeExclude",
  "subtype",
  "subtypeExclude",
  "typeSubtype",
  "typeSubtypeExclude",
  "category",
  "categoryExclude",
  "page",
  "limit",
  "query",
  "externalIntegration",
  "externalIntegrationExclude",
  "enrichment",
  "enrichmentIntegrationId",
  "enrichmentIntegrationIdExclude",
  "metadata",
  "metadataExclude",
  "firstSeenAtFrom",
  "firstSeenAtExclude",
  "firstSeenAtTo",
  "lifeSpanFrom",
  "lifeSpanTo",
  "lifeSpanExclude",
  "service",
  "serviceExclude",
];
